import cx from 'classnames';
import moment from 'moment';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {DataWrapper, FormattedMessage, Icon} from 'components';
import {SessionTextChatMessageOwner} from 'enums';
import {AnyParticipant, Session} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {TextChatContentType, TextChatMessage} from 'types';
import {sessionUtils} from 'utils';
import {buildTranslationKey} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT;

type Props = {
    message: Partial<TextChatMessage>;
    participants: AnyParticipant[];
    localParticipantId: string;
    isDownloading: boolean;
    downloadFileDisabled: boolean;
    conversationMode?: boolean;
    downloadFile: (message: Partial<TextChatMessage>) => void;
    renderIcon: (message: Partial<TextChatMessage>) => React.ReactElement;
    getTranslation?: (message: Partial<TextChatMessage>) => string;
}

export const TextChatMessageItem: React.FC<Props> = ({
    message, localParticipantId, isDownloading, downloadFileDisabled, participants,
    renderIcon, conversationMode, downloadFile, getTranslation
}) => {
    const {t} = useTranslation();
    const messageOwner = useMemo(() =>
        sessionUtils.getTextChatMessageOwner(message.from?.id, localParticipantId), [message, localParticipantId]);
    const isInteractiveChatAnswer = useMemo(() => [
        TextChatContentType.InteractiveChatAnswer,
        TextChatContentType.ChatbotConfirmation
    ].includes(message.contentType), [message]);

    const translatedMessage = useMemo(() => {
        if (getTranslation && message.contentType === TextChatContentType.Text) {
            return getTranslation(message);
        }
        return message.content;
    }, [getTranslation, message]);

    const renderFileMessage = useCallback(() => (
        <DataWrapper
            isLoading={isDownloading}
            loadingMinHeight={false}
            svgClassName='tw-max-h-8'
            hideContentOnLoading={false}
        >
            <div
                className={cx('file',
                    {disabled: downloadFileDisabled},
                    {loading: isDownloading},
                )}
                onClick={() => !downloadFileDisabled && !isDownloading && downloadFile(message)}
            >
                <Icon
                    name='file-text'
                    className='tw-mr-2'
                    color={downloadFileDisabled ? 'default' : 'primary'}
                />
                <span className='file-name'>{message.content}</span>
            </div>
        </DataWrapper>
    ), [downloadFileDisabled, isDownloading, downloadFile, message]);

    const isClientLeftRight = conversationMode && messageOwner !== SessionTextChatMessageOwner.Foreign;
    const showIconBefore = conversationMode
        ? messageOwner === SessionTextChatMessageOwner.Foreign
        : messageOwner !== SessionTextChatMessageOwner.Own;

    return (
        <div className={cx('message', messageOwner, {'conversation-mode': isClientLeftRight})}>
            {showIconBefore &&
                <div className='message-icon'>{renderIcon(message)}</div>}
            <div className='tw-flex tw-flex-col tw-max-w-[75%]'>
                <div className='tw-break-words tw-overflow-hidden'>
                    {(messageOwner === SessionTextChatMessageOwner.Foreign || messageOwner === SessionTextChatMessageOwner.Own) &&
                        <div className='message-participant'>
                            {
                                sessionUtils.getParticipantNameWithMeById(t, {participants} as Session<AnyParticipant>, message.from.id)
                            }
                        </div>}
                    <div className={cx('message-content', {
                        foreign: messageOwner === SessionTextChatMessageOwner.Foreign,
                        own: messageOwner === SessionTextChatMessageOwner.Own,
                        'interactive-chat-answer': isInteractiveChatAnswer,
                        system: messageOwner === SessionTextChatMessageOwner.System,
                        'interactive-chat-error': message.contentType === TextChatContentType.InteractiveChatError
                    })}
                    >

                        {message.contentType === TextChatContentType.File && renderFileMessage()}
                        {message.contentType === TextChatContentType.Text &&
                            <FormattedMessage message={translatedMessage} />}
                        {message.contentType === TextChatContentType.InteractiveChat &&
                            <FormattedMessage
                                message={T_KEYS.INTERACTIVE_CHAT_CODE[buildTranslationKey(message.content)] ?
                                    t(T_KEYS.INTERACTIVE_CHAT_CODE[buildTranslationKey(message.content)]) : translatedMessage}
                            />}
                        {message.contentType === TextChatContentType.InteractiveChatAnswer &&
                            <FormattedMessage message={message.content} />}
                        {message.contentType === TextChatContentType.Chatbot &&
                            <div className='text'>
                                <FormattedMessage message={message.content} />
                            </div>}
                        {message.contentType === TextChatContentType.ChatbotConfirmation &&
                            <div className='text'>
                                <FormattedMessage message={message.content} />
                            </div>}
                        {message.contentType === TextChatContentType.InteractiveChatError &&
                            <div className='text'>
                                <FormattedMessage
                                    message={t(T_KEYS.INTERACTIVE_CHAT_ERROR[buildTranslationKey(message.content)])}
                                />
                            </div>}
                    </div>
                    <div className={cx(
                        'message-time',
                        messageOwner === SessionTextChatMessageOwner.Own ? 'own' : 'foreign'
                    )}
                    >
                        {/* TODO: take format from settings */}
                        {moment(message.date).format('HH:mm')}
                    </div>
                </div>
            </div>
            {isClientLeftRight &&
                <div className='message-icon message-icon--right'>{renderIcon(message)}</div>}
        </div>
    );
};